import { hasRawLoadFlowCalculations } from '@/features/execute-calculation-set/hasLoadFlowCalculations';
import { type FeatureToggles } from '@/overmind';
import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import type {
  CalculationScenarios as CalculatedScenarios,
  CompletedCalculationTactic,
  LoadFlowScenarioResults,
  VoltageLevel
} from '../..';
import type { GridElementPropertyAttentionStatusByCalculationType } from '../../evaluation/AttentionStatusRepository';
import {
  AttentionStatusStorage,
  AttentionStatusStorage2
} from '../../evaluation/AttentionStatusRepository';
import type { EvaluationRule } from '../../evaluation/EvaluationRule';
import { RulesProvider } from '../../evaluation/RulesProvider';
import type { GridElementsDetailsByCalculationType } from './GridElementsDetails';
import { createLvLoadFlowDetails, createMvLoadFlowDetails } from './loadFlow';
import {
  createLvShortCircuitDetails,
  createMvShortCircuitDetails
} from './shortCircuit';
import { Features } from '../../createGridElementCalculationSetExecutionDetails';

export const evaluateByCalculationType = (
  features: Features,
  calculatedScenarios: CalculatedScenarios,
  evaluationRules: EvaluationRule[],
  calculationTactic: CompletedCalculationTactic,
  loadFlowFilterFunction: (results: LoadFlowScenarioResults) => boolean,
  voltageLevel: VoltageLevel,
  featureToggles?: FeatureToggles
): GridElementsDetailsByCalculationType & AttentionStatuses => {
  const rulesProvider = RulesProvider.fromRules(evaluationRules);
  const loadFlowAttentionStatusRepository = AttentionStatusStorage.create();

  const shortCircuitScenarioIds = calculatedScenarios.shortCircuit.reduce(
    (scenariosString, scenario) =>
      scenariosString === ''
        ? scenario.scenarioId.toString()
        : scenariosString + ', ' + scenario.scenarioId.toString(),
    ''
  );
  const shortCircuitAttentionStatusRepository = AttentionStatusStorage2.create(
    shortCircuitScenarioIds
  );

  switch (voltageLevel) {
    case 'LowVoltage':
      return lowVoltage();
    case 'MediumVoltage':
      return mediumVoltage();
    default:
      throw new Error('Unknown voltage level');
  }

  function lowVoltage() {
    const gridElementsEvaluatedShortCircuitResults =
      createLvShortCircuitDetails(
        features,
        calculatedScenarios.shortCircuit.flatMap(
          scenarios => scenarios.results
        ),
        rulesProvider,
        shortCircuitAttentionStatusRepository,
        featureToggles
      );

    if (hasRawLoadFlowCalculations(calculationTactic)) {
      const gridElementPropertyAttentionStatus = {
        loadFlow: [],
        shortCircuit: shortCircuitAttentionStatusRepository.toState()
      };
      return {
        loadFlow: [],
        shortCircuit: gridElementsEvaluatedShortCircuitResults,
        gridElementPropertyAttentionStatus
      };
    }

    const filteredLoadFlow = calculatedScenarios.loadFlow.filter(
      loadFlowFilterFunction
    );

    const gridElementsEvaluatedLoadFlowResults = createLvLoadFlowDetails(
      features,
      filteredLoadFlow,
      rulesProvider,
      calculationTactic,
      loadFlowAttentionStatusRepository
    );

    const gridElementPropertyAttentionStatus = {
      loadFlow: loadFlowAttentionStatusRepository.toState(),
      shortCircuit: shortCircuitAttentionStatusRepository.toState()
    };
    return {
      loadFlow: gridElementsEvaluatedLoadFlowResults,
      shortCircuit: gridElementsEvaluatedShortCircuitResults,
      gridElementPropertyAttentionStatus
    };
  }

  function mediumVoltage() {
    const gridElementsLoadFlowDetails = createMvLoadFlowDetails(
      features,
      calculatedScenarios.loadFlow.filter(loadFlowFilterFunction),
      rulesProvider,
      calculationTactic,
      loadFlowAttentionStatusRepository
    );
    const gridElementsEvaluatedShortCircuitResults =
      createMvShortCircuitDetails(
        features,
        calculatedScenarios.shortCircuit,
        calculationTactic.calculationSetResult.grid.functional,
        rulesProvider,
        shortCircuitAttentionStatusRepository,
        featureToggles
      );

    const gridElementPropertyAttentionStatus = {
      loadFlow: loadFlowAttentionStatusRepository.toState(),
      shortCircuit: shortCircuitAttentionStatusRepository.toState()
    };

    return {
      loadFlow: gridElementsLoadFlowDetails,
      shortCircuit: gridElementsEvaluatedShortCircuitResults,
      gridElementPropertyAttentionStatus
    };
  }
};

export type AttentionStatuses = {
  gridElementPropertyAttentionStatus: GridElementPropertyAttentionStatusByCalculationType;
};
